import React from 'react';
import { Grid, Typography, Button, IconButton } from '@mui/material';
import { Control, useForm, useWatch } from 'react-hook-form';
import { AppBox } from '../../components/AppBox';
import { AppAutocompleteField } from '../../components/AppAutocompleteField';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { GroupRoutinesServices } from '../../services/grouproutines.service';
import { WorkoutGroupRoutineType } from '../../types/workout.type';
import { AppFieldProps } from '../../types/components.type';

const PHASES: WorkoutGroupRoutineType["phase"][] = [
    "pod",
    "lap",
    "start",
    "end"
];

type WorkoutGroupRoutinesFieldProps = {
    control: Control<any>;
} & AppFieldProps;

export default function WorkoutGroupRoutinesField({ control, setValue }: WorkoutGroupRoutinesFieldProps) {
    const controlName = 'workout_group_routines';
    const routines = useWatch({
        control,
        name: controlName,
        defaultValue: []
    }) as WorkoutGroupRoutineType[];

    const addGroupRoutine = () => {
        if (routines.length >= PHASES.length) return; // Max 4

        const newRoutines = [...routines, { group_routine_id: null, phase: null }];

        setValue?.(controlName, newRoutines, { shouldValidate: true });
    };

    const removeGroupRoutine = (idx: number) => {
        const newRoutines = routines.filter((_: unknown, i: number) => i !== idx);
        setValue?.(controlName, newRoutines, { shouldValidate: true });
    };

    const usedPhases = routines.map(r => r.phase).filter(Boolean);

    return (
        <AppBox p={2} border={1} borderColor="grey.300">
            <Typography variant="subtitle1" gutterBottom>
                Group Routines
            </Typography>
            <Grid container spacing={2}>
                {routines.map((gr: WorkoutGroupRoutineType, idx: number) => (
                    <Grid item xs={12} key={idx}>
                        <AppBox>
                            <Grid container spacing={2} alignItems="center">

                                {/* Routine selector */}
                                <Grid item xs={8} sm={6}>
                                    <AppAutocompleteField
                                        multiple={false}
                                        label='Group Routine'
                                        name={`workout_group_routines.${idx}.group_routine_id`}
                                        control={control}
                                        service={{
                                            load: GroupRoutinesServices.getAllWorkoutRoutines
                                        }}
                                    />
                                </Grid>

                                {/* Phase selector */}
                                <Grid item xs={4} sm={4}>
                                    <AppAutocompleteField
                                        multiple={false}
                                        name={`workout_group_routines.${idx}.phase`}
                                        label='Phase'
                                        control={control}
                                        service={{
                                            options: PHASES
                                                .filter(p => p === gr.phase || !usedPhases.includes(p))
                                                .map(p => ({
                                                    id: p,
                                                    name: p
                                                }))
                                        }}
                                    />
                                </Grid>

                                {/* Delete button */}
                                <Grid item xs={12} sm={2}>
                                    <IconButton
                                        aria-label="delete"
                                        color="error"
                                        onClick={() => removeGroupRoutine(idx)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>

                            </Grid>
                        </AppBox>
                    </Grid>
                ))}

                {/* Global add button */}
                <Grid item xs={12}>
                    <Button
                        onClick={addGroupRoutine}
                        variant="contained"
                        color="primary"
                        disabled={routines.length >= PHASES.length}
                    >
                        Add Group Routine
                    </Button>
                </Grid>
            </Grid>
        </AppBox>
    );
}